"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { signIn, useSession } from "next-auth/react";
import { AuthMenuTrigger } from "./AuthMenuTrigger";
import type { AuthMenuTriggerAs } from "./types";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  Spinner,
} from "@/components/ui";
import NextLink from "next/link";
import { menuSectionsAuth } from "@/config/navigation";
import { filterMenuSectionsAuth } from "@/lib/claims/menu-filter";
import {
  menuClaimsForScopedProjects,
  menuContextLabelFromSession,
  menuSidebarSubtitleFromSession,
} from "@/lib/auth/session-menu-claims";
import { useActiveProject } from "@/context/active-project";
import { useActiveOrganization } from "@/context/active-organization";
import { useTranslate } from "@tolgee/react";

export type AuthButtonProps = {
  callbackUrl?: string;
  triggerAs?: AuthMenuTriggerAs;
};

export default function AuthButton({ callbackUrl, triggerAs = "navbar" }: AuthButtonProps) {
  const { data: session, status } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslate();
  const { activeProjectId, projects } = useActiveProject();
  const { activeOrganizationId } = useActiveOrganization();
  if (status === "loading") return null;

  if (session) {
    const menuSide = triggerAs === "sidebar" ? "top" : "bottom";
    const menuAlign = triggerAs === "sidebar" ? "start" : "end";

    const menuItemsAuth = filterMenuSectionsAuth(
      menuSectionsAuth,
      menuContextLabelFromSession(session, activeOrganizationId),
      menuClaimsForScopedProjects(session, activeProjectId, projects),
      session?.user?.organizations ?? [],
      session.user.projects ?? [],
      activeOrganizationId,
    );

    const sidebarSubtitle =
      menuSidebarSubtitleFromSession(
        session,
        t("usermenu.organizationadmin"),
        activeOrganizationId,
      ) || "—";

    return (
      <DropdownMenu>
        <AuthMenuTrigger
          triggerAs={triggerAs}
          user={session.user}
          userDisplayName={session.user?.name ?? t("auth.user")}
          sidebarSubtitle={sidebarSubtitle}
        />

        <DropdownMenuContent side={menuSide} align={menuAlign} className="min-w-64">
          {session.user?.name ? (
            <>
              <div className="px-2 py-1.5">
                <div className="text-sm font-medium">{session.user.name}</div>
                <div className="text-xs text-muted-foreground">{session.user.email}</div>
              </div>
              <DropdownMenuSeparator />
            </>
          ) : null}

          {menuItemsAuth.map((section, index) => (
            <DropdownMenuGroup key={section.labelKey + index.toString()}>
              <DropdownMenuSeparator />
              {section.labelKey ? (
                <DropdownMenuLabel>{t(section.labelKey)}</DropdownMenuLabel>
              ) : null}
              {section.items.map((item) => {
                const href = item.href || "";
                const isExternal = href.startsWith("http");
                const label = t(item.labelKey);

                if (isExternal) {
                  return (
                    <DropdownMenuItem
                      key={item.labelKey}
                      render={<NextLink href={href} target="_blank" rel="noopener noreferrer" />}
                    >
                      <Icon icon={item.icon} />
                      {label}
                      <Icon icon="lucide:external-link" className="ml-auto size-4 opacity-60" />
                    </DropdownMenuItem>
                  );
                }

                return (
                  <DropdownMenuItem
                    key={item.href + item.labelKey}
                    render={<NextLink href={href} />}
                  >
                    <Icon icon={item.icon} />
                    {label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (triggerAs === "sidebar") return null;

  const handleLogin = async () => {
    setIsLoading(true);
    await signIn("waas", { callbackUrl: callbackUrl || "/dashboard" });
  };

  return (
    <Button
      onClick={handleLogin}
      variant="default"
      disabled={isLoading}
      size="sm"
      className="w-full"
    >
      {isLoading ? (
        <>
          <Spinner />
          {t("auth.signingin")}
        </>
      ) : (
        t("auth.login")
      )}
    </Button>
  );
}
